'use client';

import { FC } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import type { MediaId } from 'models/Media';
import { useMediaUrl } from 'hooks/useMediaUrl';

interface Props {
  // The video to play, or null when the viewer is closed.
  mediaId: MediaId | null;
  onClose: () => void;
}

// Full-screen video player over a dark backdrop, like the Mac app's player
// window. Esc or the close button closes it.
export const VideoViewer: FC<Props> = ({ mediaId, onClose }) => {
  const t = useTranslations();
  const { url, onError } = useMediaUrl(mediaId ?? undefined);

  return (
    <Dialog
      open={mediaId !== null}
      onClose={onClose}
      className="relative z-[70]"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/90" />
      <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-10">
        <DialogPanel className="relative flex max-h-full w-full max-w-5xl items-center justify-center">
          {url && (
            <video
              controls
              autoPlay
              playsInline
              src={url}
              onError={onError}
              className="max-h-[85vh] w-full rounded-xl bg-black object-contain focus:outline-hidden"
            />
          )}
        </DialogPanel>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className={styles.close}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    </Dialog>
  );
};

// Always dark, whatever the app theme: it's a video player.
const styles = {
  backdrop: classNames(
    'fixed inset-0 transform-gpu bg-black/85 transition-opacity duration-200',
    'data-closed:opacity-0',
  ),
  container:
    'fixed inset-0 flex transform-gpu items-center justify-center p-4 sm:p-10',
  panel:
    'relative flex max-h-full w-full max-w-5xl transform-gpu items-center justify-center',
  close:
    'absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-hidden',
};
